// frontend/src/components/layouts/VisitorLayout.tsx
import React, { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, ChevronDown } from 'react-feather';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import ThemeSelector from '../../pages/visitor/ThemeSelector';
import CurrencySelector from '../../pages/visitor/CurrencySelector';
import { motion, AnimatePresence } from 'framer-motion';
import { Couple } from '../../types';
import logo from '../../asset/logo4.png'; // Import the logo

interface VisitorLayoutProps {
  children: ReactNode;
  couple?: Couple | null;
}

const VisitorLayout: React.FC<VisitorLayoutProps> = ({ children, couple }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { darkMode, colorTheme } = useTheme();
  const { currency } = useCurrency();
  const navigate = useNavigate();

  // Theme-dependent dynamic classes
  const primaryBgClass = getThemeBgClass(colorTheme, 'primary');
  const accentBgClass = getThemeBgClass(colorTheme, 'accent');
  const textClass = getThemeTextClass(colorTheme);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [navigate]);

  // Navigation links
  const navLinks = [
    { text: 'Home', href: `/wedding/${couple?.customUrl || ''}` },
    { text: 'Events', href: `/wedding/${couple?.customUrl || ''}/events` },
    { text: 'RSVP', href: `/wedding/${couple?.customUrl || ''}/rsvp` },
    { text: 'Registry', href: `/wedding/${couple?.customUrl || ''}/registry` },
    { text: 'Photos', href: `/wedding/${couple?.customUrl || ''}/photos` },
    { text: 'Contribute', href: `/wedding/${couple?.customUrl || ''}/contribute` },
  ];

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Navbar */}
      <header className={`relative shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo Only */}
            <div className="flex-shrink-0">
              <Link 
                to={couple ? `/wedding/${couple.customUrl}` : '/'} 
                className="flex items-center"
              >
                <img 
                  src={logo} 
                  alt="TaleOfTwo" 
                  className="h-10 md:h-12 w-auto object-contain"
                />
              </Link>
            </div>

            {/* Desktop navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.text}
                  to={link.href}
                  className={`text-gray-600 dark:text-gray-300 hover:${textClass} transition-colors duration-200`}
                >
                  {link.text}
                </Link>
              ))}
            </nav>

            {/* Theme and currency controls */}
            <div className="hidden md:flex items-center space-x-2">
              <ThemeSelector />
              <CurrencySelector />
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                aria-expanded={isMenuOpen}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? 
                  <X size={24} className="text-gray-600 dark:text-gray-300" /> : 
                  <Menu size={24} className="text-gray-600 dark:text-gray-300" />
                }
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className={`md:hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
            >
              <div className="px-4 py-3 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.text}
                    to={link.href}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    {link.text}
                  </Link>
                ))}
                <div className="pt-4 pb-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between px-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Preferences
                    </p>
                  </div>
                  <div className="mt-3 px-3 flex items-center justify-between">
                    <ThemeSelector />
                    <CurrencySelector />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="mb-4">
                <img src={logo} alt="TaleOfTwo" className="h-8 w-auto object-contain" />
              </div>
              <p className="text-gray-600 dark:text-gray-400">Making wedding planning simple and beautiful.</p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 text-gray-700 dark:text-gray-300">Features</h4>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li>Wedding Website</li>
                <li>Guest Management</li>
                <li>Gift Registry</li>
                <li>Todo Lists</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 text-gray-700 dark:text-gray-300">Legal</h4>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li><Link to="/about" className="hover:underline">About Us</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                <li><Link to="/privacy" className="hover:underline">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:underline">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 text-gray-700 dark:text-gray-300">Account</h4>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li><Link to="/login" className="hover:underline">Login</Link></li>
                <li><Link to="/register" className="hover:underline">Register</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400">
            <p>&copy; {new Date().getFullYear()} TaleOfTwo. All rights reserved.</p>
            <div className="mt-2 flex justify-center items-center space-x-2">
              <span>Theme: {getThemeName(colorTheme)}</span>
              <span>•</span>
              <span>Currency: {currency.code}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Helper functions to get theme-specific classes
function getThemeBgClass(theme: string, variant: 'primary' | 'accent'): string {
  const classes: Record<string, Record<string, string>> = {
    rose: {
      primary: 'bg-rose-500 dark:bg-rose-600',
      accent: 'bg-rose-100 dark:bg-rose-900'
    },
    blue: {
      primary: 'bg-blue-500 dark:bg-blue-600',
      accent: 'bg-blue-100 dark:bg-blue-900'
    },
    purple: {
      primary: 'bg-purple-500 dark:bg-purple-600',
      accent: 'bg-purple-100 dark:bg-purple-900'
    },
    emerald: {
      primary: 'bg-emerald-500 dark:bg-emerald-600',
      accent: 'bg-emerald-100 dark:bg-emerald-900'
    },
    amber: {
      primary: 'bg-amber-500 dark:bg-amber-600',
      accent: 'bg-amber-100 dark:bg-amber-900'
    }
  };
  return classes[theme]?.[variant] || classes.rose[variant];
}

function getThemeTextClass(theme: string): string {
  const classes: Record<string, string> = {
    rose: 'text-rose-500 dark:text-rose-400',
    blue: 'text-blue-500 dark:text-blue-400',
    purple: 'text-purple-500 dark:text-purple-400',
    emerald: 'text-emerald-500 dark:text-emerald-400',
    amber: 'text-amber-500 dark:text-amber-400'
  };
  return classes[theme] || classes.rose;
}

function getThemeName(theme: string): string {
  const names: Record<string, string> = {
    rose: 'Rose',
    blue: 'Ocean',
    purple: 'Lavender',
    emerald: 'Emerald',
    amber: 'Golden'
  };
  return names[theme] || names.rose;
}

export default VisitorLayout;